import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Save, Play, Users, Zap, Clock, AlertCircle, HelpCircle, X } from "lucide-react";
import Layout from "../components/Layout";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("default", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

export default function AdminAutoAssign() {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === "super_admin";

  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);

  const [enabled, setEnabled] = useState(false);
  const [waitMin, setWaitMin] = useState(90);
  const [sourceEmail, setSourceEmail] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/auto-assign");
      setDashboard(data);
      setEnabled(!!data.config?.enabled);
      setWaitMin(Number(data.config?.wait_minutes || 90));
      setSourceEmail(data.config?.source_owner_email || "");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/admin/auto-assign", {
        enabled,
        wait_minutes: Number(waitMin),
        source_owner_email: sourceEmail.trim().toLowerCase() || null,
      });
      toast.success("Settings saved");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const runNow = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/admin/auto-assign/run-now");
      if (data.skipped) {
        toast.error(`Skipped: ${data.skipped}`);
      } else {
        toast.success(`Assigned ${data.assigned || 0} lead(s)`);
      }
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Run failed");
    } finally {
      setRunning(false);
    }
  };

  const queue = dashboard?.queue || {};
  const agents = dashboard?.eligible_agents || [];

  const [breakdown, setBreakdown] = useState(null);
  const [breakdownAgent, setBreakdownAgent] = useState(null);
  const [breakdownLoading, setBreakdownLoading] = useState(false);

  const openBreakdown = async (agent) => {
    setBreakdownAgent(agent);
    setBreakdownLoading(true);
    setBreakdown(null);
    try {
      const { data } = await api.get(`/admin/auto-assign/breakdown/${agent.id}`);
      setBreakdown(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to load breakdown");
      setBreakdownAgent(null);
    } finally {
      setBreakdownLoading(false);
    }
  };

  return (
    <Layout title="Auto Lead Assignment" breadcrumb={["Admin", "Auto Lead Assignment"]}>
      {loading ? (
        <div className="text-center py-16 text-sm text-slate-500">Loading…</div>
      ) : (
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-5">
          {/* ── Left col: config + status ─────────────────────────────── */}
          <div className="xl:col-span-2 space-y-5">
            {/* Config card */}
            <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm" data-testid="auto-assign-config-card">
              <div className="flex items-start gap-3 mb-4">
                <div className={`rounded-lg p-2 ${enabled ? "bg-emerald-500" : "bg-slate-400"} text-white`}>
                  <Zap size={18} />
                </div>
                <div className="flex-1">
                  <h3 className="text-base font-semibold text-slate-900">Settings</h3>
                  <p className="text-xs text-slate-600 mt-0.5">
                    Automatically hand new leads sitting on the queue owner to eligible agents on a weighted round-robin.
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${enabled ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-slate-100 text-slate-500 border border-slate-200"}`}>
                    {enabled ? "Enabled" : "Disabled"}
                  </span>
                </div>
              </div>

              <div className="space-y-4">
                <label className="flex items-center gap-2 select-none cursor-pointer">
                  <input
                    data-testid="config-enabled"
                    type="checkbox"
                    checked={enabled}
                    onChange={(e) => setEnabled(e.target.checked)}
                    disabled={!isSuperAdmin}
                    className="w-4 h-4 accent-orange-500"
                  />
                  <span className="text-sm text-slate-800">
                    Enable auto-assignment (evaluates the queue every 5 minutes)
                  </span>
                </label>

                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">
                    Source owner email (the &quot;queue&quot; account)
                  </label>
                  <input
                    data-testid="config-source-email"
                    type="email"
                    value={sourceEmail}
                    onChange={(e) => setSourceEmail(e.target.value)}
                    disabled={!isSuperAdmin}
                    placeholder="shreya@shipmaxx.in"
                    className="w-full px-3 py-2 text-sm rounded-md border border-slate-200 bg-white"
                  />
                  <div className="mt-1 text-[11px] text-slate-500">
                    Only leads currently owned by this user (and in stage New / Not Connected) are candidates for auto-assignment.
                    {queue.source_owner_email && !queue.source_owner_found && (
                      <span className="ml-2 text-rose-600 font-medium">
                        <AlertCircle size={11} className="inline mr-0.5" /> No user found with this email
                      </span>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">
                    Wait time before auto-assign (minutes)
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      data-testid="config-wait-min"
                      type="number"
                      min={0}
                      max={1440}
                      value={waitMin}
                      onChange={(e) => setWaitMin(e.target.value)}
                      disabled={!isSuperAdmin}
                      className="w-32 px-3 py-2 text-sm rounded-md border border-slate-200 bg-white"
                    />
                    <span className="text-xs text-slate-500">min ({(Number(waitMin) / 60).toFixed(2)} h)</span>
                  </div>
                  <div className="mt-1 text-[11px] text-slate-500">
                    Applies to leads NOT yet AI-verified. AI-verified leads are assigned immediately (no wait).
                  </div>
                </div>

                {isSuperAdmin && (
                  <div className="pt-2 flex items-center gap-2">
                    <button
                      data-testid="config-save"
                      onClick={save}
                      disabled={saving}
                      className="text-sm font-medium px-4 py-2 rounded-md bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white inline-flex items-center gap-1.5"
                    >
                      <Save size={13} /> {saving ? "Saving…" : "Save settings"}
                    </button>
                    <button
                      data-testid="config-run-now"
                      onClick={runNow}
                      disabled={running}
                      title="Runs one round of assignment right now (ignores the enabled flag)"
                      className="text-sm font-medium px-4 py-2 rounded-md bg-slate-100 hover:bg-slate-200 disabled:opacity-50 text-slate-800 inline-flex items-center gap-1.5"
                    >
                      <Play size={13} /> {running ? "Running…" : "Run once now"}
                    </button>
                  </div>
                )}
                {!isSuperAdmin && (
                  <div className="text-[11px] text-slate-500 italic pt-1">
                    Only super_admin can change these settings.
                  </div>
                )}
              </div>
            </div>

            {/* Queue status card */}
            <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm" data-testid="queue-status-card">
              <div className="flex items-center gap-3 mb-4">
                <div className="bg-orange-500 text-white rounded-lg p-2"><Clock size={18} /></div>
                <div>
                  <h3 className="text-base font-semibold text-slate-900">Queue status</h3>
                  <p className="text-xs text-slate-600">Current inbox on <b>{queue.source_owner_email || "—"}</b></p>
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <StatBox label="Total in queue" value={queue.total_in_queue || 0} color="slate" testid="stat-total" />
                <StatBox label="Immediate ready (AI-verified)" value={queue.immediate_ready || 0} color="emerald" testid="stat-immediate" />
                <StatBox label="Waited ready" value={queue.waited_ready || 0} color="orange" testid="stat-waited" />
                <StatBox label="Still waiting" value={queue.waiting || 0} color="slate" testid="stat-waiting" />
              </div>
            </div>
          </div>

          {/* ── Right col: eligible agents load ────────────────────── */}
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-2">
              <Users size={16} className="text-orange-500" />
              <h3 className="text-base font-semibold text-slate-900">Eligible agents ({agents.length})</h3>
            </div>
            {agents.length === 0 ? (
              <div className="p-8 text-center text-sm text-slate-500">
                No agents in the pool yet.
                <br />
                <span className="text-xs">Tag agents as &quot;In pool&quot; from the Users &amp; Roles page.</span>
              </div>
            ) : (
              <table className="w-full text-sm" data-testid="eligible-agents-table">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Agent</th>
                    <th className="px-4 py-2 text-right text-xs font-semibold uppercase tracking-wider text-slate-500">Working</th>
                    <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Last assign</th>
                  </tr>
                </thead>
                <tbody>
                  {agents.map((a, i) => (
                    <tr key={a.id} className={`border-t border-slate-100 ${i === 0 ? "bg-orange-50/40" : ""}`} data-testid={`agent-row-${a.id}`}>
                      <td className="px-4 py-3">
                        <div className="font-medium text-slate-800 flex items-center gap-1">
                          {a.name}
                          {i === 0 && (
                            <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-orange-500 text-white uppercase" title="Next in line to receive a lead">
                              Next
                            </span>
                          )}
                        </div>
                        <div className="text-[11px] text-slate-500">{a.email}</div>
                      </td>
                      <td className="px-4 py-3 text-right font-semibold tabular-nums text-slate-800" data-testid={`agent-load-${a.id}`}>
                        <span className="mr-1">{a.working_leads}</span>
                        <button
                          onClick={() => openBreakdown(a)}
                          data-testid={`agent-why-${a.id}`}
                          className="inline-flex items-center text-slate-400 hover:text-orange-600"
                          title="Why this number?"
                        >
                          <HelpCircle size={13} />
                        </button>
                      </td>
                      <td className="px-4 py-3 text-[11px] text-slate-500">{fmtDate(a.last_auto_assigned_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* Breakdown modal */}
      {breakdownAgent && (
        <div
          className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setBreakdownAgent(null)}
          data-testid="breakdown-modal"
        >
          <div
            className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[80vh] overflow-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between sticky top-0 bg-white">
              <div>
                <h3 className="text-base font-semibold text-slate-900">
                  Working-leads breakdown · {breakdownAgent.name}
                </h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  Grouped by (stage × tag). Green rows count toward the working total.
                </p>
              </div>
              <button
                onClick={() => setBreakdownAgent(null)}
                className="p-1.5 rounded hover:bg-slate-100 text-slate-500"
                data-testid="breakdown-close"
              >
                <X size={16} />
              </button>
            </div>
            <div className="p-5">
              {breakdownLoading ? (
                <div className="text-center py-8 text-sm text-slate-500">Loading…</div>
              ) : !breakdown ? (
                <div className="text-center py-8 text-sm text-slate-500">No data.</div>
              ) : (
                <>
                  <div className="grid grid-cols-3 gap-3 mb-4">
                    <StatBox label="Working" value={breakdown.total_working} color="emerald" testid="bd-working" />
                    <StatBox label="Excluded (Cold/Lost)" value={breakdown.total_excluded} color="orange" testid="bd-excluded" />
                    <StatBox label="Total in scope (6 stages)" value={breakdown.grand_total_leads_for_owner} color="slate" testid="bd-total" />
                  </div>
                  <table className="w-full text-sm" data-testid="breakdown-table">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Stage</th>
                        <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Tag</th>
                        <th className="px-3 py-2 text-right text-xs font-semibold uppercase tracking-wider text-slate-500">Count</th>
                        <th className="px-3 py-2 text-center text-xs font-semibold uppercase tracking-wider text-slate-500">Counted?</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(breakdown.by_stage_tag || []).map((r, i) => (
                        <tr key={`${r.stage}__${r.tag}__${i}`} className={`border-t border-slate-100 ${r.counted_as_working ? "bg-emerald-50/30" : ""}`}>
                          <td className="px-3 py-2 font-medium text-slate-800">{r.stage}</td>
                          <td className="px-3 py-2 text-slate-600">{r.tag}</td>
                          <td className="px-3 py-2 text-right tabular-nums font-semibold text-slate-800">{r.count}</td>
                          <td className="px-3 py-2 text-center">
                            {r.counted_as_working ? (
                              <span className="text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5 rounded-full">Yes</span>
                            ) : (
                              <span className="text-xs font-medium bg-slate-100 text-slate-500 border border-slate-200 px-2 py-0.5 rounded-full">Excluded</span>
                            )}
                          </td>
                        </tr>
                      ))}
                      {(breakdown.by_stage_tag || []).length === 0 && (
                        <tr><td colSpan={4} className="text-center py-6 text-sm text-slate-500">No leads in scope.</td></tr>
                      )}
                    </tbody>
                  </table>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}

function StatBox({ label, value, color = "slate", testid }) {
  const bg = {
    slate: "bg-slate-50 border-slate-200",
    emerald: "bg-emerald-50 border-emerald-200",
    orange: "bg-orange-50 border-orange-200",
  }[color] || "bg-slate-50 border-slate-200";
  const text = {
    slate: "text-slate-800",
    emerald: "text-emerald-700",
    orange: "text-orange-600",
  }[color] || "text-slate-800";
  return (
    <div className={`rounded-lg border ${bg} px-4 py-3`} data-testid={testid}>
      <div className={`text-2xl font-bold tabular-nums ${text}`}>{value}</div>
      <div className="text-[11px] uppercase tracking-wider text-slate-500 mt-0.5">{label}</div>
    </div>
  );
}
